import React, { ChangeEvent } from 'react';
import { Loader2 } from 'lucide-react';
import CustomModal from '../../common/CustomModal';
import { Bank } from '../../../features/program/ProgramSlice';
import { PrincipalWalletProgramOption } from '../../../features/principal/types';

interface FormData {
  remark: string;
  amount: string;
  reference: string;
  bankId: string;
}

interface FormErrors {
  remark: string;
  amount: string;
  reference: string;
  bankId: string;
  programId: string;
}

interface PrincipalTopUpFormProps {
  show: boolean;
  onHide: () => void;
  formData: FormData;
  formErrors: FormErrors;
  selectedProgramId: string;
  programs: PrincipalWalletProgramOption[] | null;
  programsLoading: boolean;
  programsError: string | null;
  isLoading: boolean;
  banks: Bank[] | null;
  banksLoading: boolean;
  banksError: string | null;
  onFormChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onProgramChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onSubmit: (e: React.MouseEvent<HTMLElement>) => void;
}

const PrincipalTopUpForm: React.FC<PrincipalTopUpFormProps> = ({
  show,
  onHide,
  formData,
  formErrors,
  selectedProgramId,
  programs,
  programsLoading,
  programsError,
  isLoading,
  banks,
  banksLoading,
  banksError,
  onFormChange,
  onProgramChange,
  onSubmit,
}) => {
  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title="Top Up Wallet"
      width="w-[500px]"
    >
      <form className="w-full space-y-4">
        <div>
          <label htmlFor="programId" className="block text-sm font-medium text-gray-700 mb-1">
            Select Program Wallet
          </label>
          <div className="relative">
            <select
              id="programId"
              value={selectedProgramId}
              onChange={onProgramChange}
              className={`block w-full px-3 py-2 border ${formErrors.programId ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
            >
              <option value="">Select a program wallet</option>
              {!programsLoading && !programsError && programs?.map((program) => (
                <option key={program.programId} value={program.programId}>
                  {program.programName}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {formErrors.programId && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.programId}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="bankId" className="block text-sm font-medium text-gray-700 mb-1">
            Bank
          </label>
          <div className="relative">
            <select
              id="bankId"
              value={formData.bankId}
              onChange={onFormChange}
              className={`block w-full px-3 py-2 border ${formErrors.bankId ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
            >
              <option value="">Select a bank</option>
              {!banksLoading && !banksError && banks?.map((bank) => (
                <option key={bank.bankId} value={String(bank.bankId)}>
                  {bank.displayName || bank.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {formErrors.bankId && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.bankId}
            </div>
          )}
          {banksLoading && (
            <div className="text-gray-500 text-sm mt-1">
              Loading banks...
            </div>
          )}
          {banksError && (
            <div className="text-red-500 text-sm mt-1">
              {banksError}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Top Up Amount
          </label>
          <input
            id="amount"
            type="number"
            min={1}
            value={formData.amount}
            onChange={onFormChange}
            placeholder="Enter Amount"
            className={`block w-full px-3 py-2 border ${formErrors.amount ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
          />
          {formErrors.amount && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.amount}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
            Bank Reference
          </label>
          <input
            id="reference"
            type="text"
            value={formData.reference}
            onChange={onFormChange}
            placeholder="Transaction Reference No. as per Bank"
            className={`block w-full px-3 py-2 border ${formErrors.reference ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
          />
          {formErrors.reference && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.reference}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="remark" className="block text-sm font-medium text-gray-700 mb-1">
            Remark (Optional)
          </label>
          <input
            id="remark"
            type="text"
            value={formData.remark}
            onChange={onFormChange}
            placeholder="Optional Remark"
            className={`block w-full px-3 py-2 border ${formErrors.remark ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
          />
          {formErrors.remark && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.remark}
            </div>
          )}
        </div>

        <div className="flex justify-between pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </div>
            ) : (
              "Top Up Wallet"
            )}
          </button>
        </div>
      </form>
    </CustomModal>
  );
};

export default PrincipalTopUpForm;

